import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  FiHeart, 
  FiShare2, 
  FiMapPin, 
  FiCalendar, 
  FiUsers, 
  FiDroplet,
  FiChevronLeft,
  FiChevronRight,
  FiArrowRight,
  FiStar,
  FiZap,
  FiCheckSquare,
  FiDollarSign,
  FiCopy,
  FiTwitter,
  FiFacebook,
  FiLinkedin
} from 'react-icons/fi'; 
import { 
  FaGasPump, 
  FaCar, 
  FaCog, 
  FaPalette,
  FaShieldAlt,
  FaTachometerAlt,
  FaHome,
  FaCode,
  FaSubway,
  FaRunning,
  FaInfoCircle,
  FaEye,
  FaWhatsapp
} from 'react-icons/fa';
import './ProfilCar.css';

import { Link, useParams } from 'react-router-dom';
import API_CONFIG2 from '../API_CONFIG2/API_CONFIG2';

const ProfilCarComp = () => {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [vus, setVus] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [showAllEquipements, setShowAllEquipements] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // AJOUT: Définition des animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        duration: 0.8
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const imageVariants = {
    hidden: { 
      opacity: 0, 
      scale: 1.1,
      rotateX: 5 
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      rotateX: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      rotateX: -5,
      transition: {
        duration: 0.4
      }
    }
  };

  const fetchCar = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_CONFIG2.apiUrl}get_vehicle.php?id=${id}`, { 
        timeout: 10000 
      });

      if (response.data.success) {
        setCar(response.data.vehicle);
         setVus(response.data.views);
      } else {
        setError(response.data.message || 'Véhicule introuvable');
      }
    } catch (err) {
      console.error('Erreur API:', err);
      setError(err.response?.data?.message || 'Impossible de charger les données du véhicule');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchCar();
  }, [id]);

  // AJOUT: Fonction de partage améliorée
  const handleShare = async (platform = 'default') => {
    const shareUrl = window.location.href;
    const shareText = `Découvrez ce ${car?.marque} ${car?.modele} - ${car?.prix?.toLocaleString()} $`;
    
    try {
      switch (platform) {
        case 'whatsapp':
          window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
          break;
        case 'twitter':
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
          break;
        case 'facebook':
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
          break;
        case 'linkedin':
          window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
          break;
        default:
          if (navigator.share) {
            await navigator.share({
              title: `${car?.marque} ${car?.modele}`,
              text: shareText,
              url: shareUrl,
            });
          } else {
            await navigator.clipboard.writeText(shareUrl);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 3000);
          }
      }
    } catch (error) {
      console.log('Erreur de partage:', error);
      // Fallback pour copier dans le presse-papier
      try {
        await navigator.clipboard.writeText(shareUrl);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      } catch (err) {
        console.error('Erreur de copie:', err);
      }
    }
  };

  // AJOUT: Composant de particules spectaculaire
  const ParticlesBackground = () => (
    <div className="particles-background">
      {[...Array(25)].map((_, i) => (
        <div 
          key={i}
          className="particle" 
          style={{
            '--i': i,
            '--random-x': Math.random() * 100,
            '--random-y': Math.random() * 100,
            '--random-size': Math.random() * 4 + 1,
            '--random-duration': Math.random() * 25 + 15,
            '--random-delay': Math.random() * -30,
            '--random-color': Math.random() > 0.7 ? 'rgba(100, 200, 255, 0.6)' : 'rgba(255, 255, 255, 0.3)'
          }}
        />
      ))}
      {/* Particules spéciales pour effet de profondeur */}
      {[...Array(10)].map((_, i) => (
        <div 
          key={`large-${i}`}
          className="particle large-particle"
          style={{
            '--i': i + 25,
            '--random-x': Math.random() * 100,
            '--random-y': Math.random() * 100,
            '--random-duration': Math.random() * 40 + 30,
            '--random-delay': Math.random() * -50
          }}
        />
      ))}
    </div>
  );

  // AJOUT: Modal de partage
  const ShareModal = () => (
    <AnimatePresence>
      {showShareModal && (
        <motion.div 
          className="share-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowShareModal(false)}
        >
          <motion.div 
            className="share-modal-content"
            initial={{ scale: 0.8, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 50 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="share-modal-header">
              <h3>Partager ce véhicule</h3>
              <button 
                className="close-modal"
                onClick={() => setShowShareModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="share-platforms">
              {[
                { platform: 'whatsapp', icon: FaWhatsapp, color: '#25D366', label: 'WhatsApp' },
                { platform: 'facebook', icon: FiFacebook, color: '#1877F2', label: 'Facebook' },
                { platform: 'twitter', icon: FiTwitter, color: '#1DA1F2', label: 'Twitter' },
                { platform: 'linkedin', icon: FiLinkedin, color: '#0A66C2', label: 'LinkedIn' },
                { platform: 'copy', icon: FiCopy, color: '#6B7280', label: 'Copier le lien' },
              ].map(({ platform, icon: Icon, color, label }) => (
                <motion.button
                  key={platform}
                  className="share-platform-btn"
                  onClick={() => {
                    handleShare(platform);
                    if (platform === 'copy') setShowShareModal(false);
                  }}
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  style={{ '--platform-color': color }}
                >
                  <Icon className="platform-icon" />
                  <span>{label}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <motion.div 
          className="loading-spinner"
          animate={{ 
            rotate: 360,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            rotate: { duration: 1, repeat: Infinity, ease: "linear" },
            scale: { duration: 1.5, repeat: Infinity }
          }}
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, repeat: Infinity, repeatType: "reverse", duration: 1 }}
        >
          Chargement du véhicule...
        </motion.p>
      </div>
    );
  }

  if (error) {
    return (
      <motion.div 
        className="error-container"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
      >
        <div className="error-icon">⚡</div>
        <h3>Erreur de chargement</h3>
        <p>{error}</p>
        <motion.button 
          className="retry-button"
          onClick={fetchCar}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Réessayer
        </motion.button>
      </motion.div>
    );
  }

  if (!car) return null;

  // Calculs pour les images, équipements et options
  const nextImage = () => setSelectedImage((prev) => (prev + 1) % car.images.length);
  const prevImage = () => setSelectedImage((prev) => (prev - 1 + car.images.length) % car.images.length);
  
  // Calculs pour les équipements et options
  const equipements = car.equipements || [];
  const options = car.options || [];
  const visibleEquipements = showAllEquipements ? equipements : equipements.slice(0, 6);
  const visibleOptions = showAllOptions ? options : options.slice(0, 6);

  return (
    <motion.div 
      className="profil-car-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* AJOUT: Helmet pour le SEO */}
      <Helmet>
        <title>{car.marque} {car.modele} - {car.prix?.toLocaleString()} $ | Parc Automobile</title>
        <meta name="description" content={`Découvrez le ${car.marque} ${car.modele} ${car.annee} - ${car.carburant} - ${car.kilometrage?.toLocaleString()} km - Prix: ${car.prix?.toLocaleString()} $`} />
        <meta property="og:title" content={`${car.marque} ${car.modele}`} />
        <meta property="og:description" content={`Véhicule ${car.annee} - ${car.carburant} - ${car.kilometrage?.toLocaleString()} km`} />
        <meta property="og:image" content={`${API_CONFIG2.apiUrl}carpic/${car.images[0]}`} />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* AJOUT: Particules en arrière-plan */}
      <ParticlesBackground />

      {/* AJOUT: Bouton de partage flottant */}
      <motion.button 
        className={`floating-share-btn ${copySuccess ? 'share-success' : ''}`}
        onClick={() => setShowShareModal(true)}
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 1, type: "spring", stiffness: 200 }}
        whileHover={{ 
          scale: 1.1,
          rotate: 10,
          transition: { type: "spring", stiffness: 300 }
        }}
        whileTap={{ scale: 0.9 }}
      >
        <FiShare2 className="share-btn-icon" />
        {copySuccess && (
          <motion.span 
            className="share-feedback-tooltip"
            initial={{ opacity: 0, y: 10, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.8 }}
          >
            Lien copié !
          </motion.span>
        )}
      </motion.button>

      {/* AJOUT: Modal de partage */}
      <ShareModal />

      {/* En-tête élégant */}
      <motion.div 
        className="car-header"
        variants={cardVariants}
        whileHover={{ y: -2 }}
      >
        <div className="header-content">
          <div className="title-section">
            <motion.div 
              className="car-badge text-left"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3, type: "spring" }}
            >
              <FiZap className="badge-icon" />
              {car.statut}
            </motion.div>
            
            <h1 className="car-title text-left">
               
              <Link to='/RentCars'><span className="brand">Retour au parc automobile</span></Link> 
              <span className="brand">{car.marque}</span>
              <span className="model">{car.modele}</span>
            </h1>
            <div className="car-subtitle">
              <span>Année : {car.annee} | </span>
             
            </div>
          </div>
          
          <div className="price-section">
            <div className="price-main">
              <motion.span 
                className="price"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: "spring" }}
              >
                {car.prix?.toLocaleString()} $
              </motion.span>
              <span className="price-label">Prix TTC</span>
            </div>
            <div className="action-buttons">
              <motion.div 
                className={`like-button ${isLiked ? 'liked' : ''}`}
                onClick={() => setIsLiked(!isLiked)}
                whileHover={{ 
                  scale: 1.1,
                  rotate: isLiked ? 0 : 10 
                }}
                whileTap={{ scale: 0.9 }}
                animate={{
                  scale: isLiked ? [1, 1.2, 1] : 1
                }}
                transition={{ duration: 0.3 }}
              >
                <FaEye />
              </motion.div> <b className='text-white'>{vus} </b>
              <motion.button 
                className="share-button"
                whileHover={{ scale: 1.1, rotate: -5 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowShareModal(true)}
              >
                <FiShare2 />
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Galerie immersive */}
      <motion.section 
        className="gallery-section"
        variants={cardVariants}
      >
        <div className="main-image-container">
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedImage}
              className="image-wrapper"
              variants={imageVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {!imageLoaded && (
                <div className="image-skeleton">
                  <div className="skeleton-shimmer" />
                </div>
              )}
              <img
                src={`${API_CONFIG2.apiUrl}carpic/${car.images[selectedImage]}`}
                alt={`${car.marque} ${car.modele}`}
                className="main-image"
                onLoad={() => setImageLoaded(true)}
                style={{ opacity: imageLoaded ? 1 : 0 }}
              />
            </motion.div>
          </AnimatePresence>

          <motion.button 
            className="nav-button prev-button"
            onClick={prevImage}
            whileHover={{ 
              scale: 1.15, 
              x: -2,
              backgroundColor: "rgba(0,0,0,0.9)" 
            }}
            whileTap={{ scale: 0.9 }}
          >
            <FiChevronLeft />
          </motion.button>
          
          <motion.button 
            className="nav-button next-button"
            onClick={nextImage}
            whileHover={{ 
              scale: 1.15, 
              x: 2,
              backgroundColor: "rgba(0,0,0,0.9)" 
            }}
            whileTap={{ scale: 0.9 }}
          >
            <FiChevronRight />
          </motion.button>

          <motion.div 
            className="image-indicator"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <span className="image-counter">{selectedImage + 1} / {car.images.length}</span>
          </motion.div>

          <div className="image-overlay">
            <motion.button 
              className="zoom-button"
              whileHover={{ scale: 1.05, y: -1 }}
              whileTap={{ scale: 0.95 }}
            >
              🔍 Voir en détail
            </motion.button>
          </div>
        </div>

        <div className="thumbnails-container">
          {car.images.map((img, i) => (
            <motion.div
              key={i}
              className={`thumbnail ${i === selectedImage ? 'active' : ''}`}
              onClick={() => setSelectedImage(i)}
              whileHover={{ 
                scale: 1.08,
                y: -3
              }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ 
                delay: 0.6 + i * 0.1,
                type: "spring",
                stiffness: 150
              }}
            >
              <img 
                src={`${API_CONFIG2.apiUrl}carpic/${img}`} 
                alt={`Vue ${i + 1}`} 
              />
              <div className="thumbnail-overlay" />
              {i === selectedImage && (
                <motion.div 
                  className="active-indicator"
                  layoutId="activeThumbnail"
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
              )}
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Grille d'information moderne */}
      <div className="info-grid">
        {/* Caractéristiques principales */}
        <motion.section 
          className="specs-card glass-card"
          variants={cardVariants}
          whileHover={{ y: -3, scale: 1.01 }}
        >
          <div className="card-header">
            <div className="icon-wrapper">
              <FaCar className="card-icon" />
            </div>
            <h3 className="section-title">Caractéristiques</h3>
          </div>
          <div className="specs-grid">
            {[
              { icon: FiCalendar, label: "Année", value: car.annee },
              { icon: FaRunning, label: "Kilométrage", value: `${car.kilometrage?.toLocaleString()} km` },
              { icon: FaGasPump, label: "Énergie", value: car.carburant },
              { icon: FaCode, label: "Couleur", value: car.immatriculation },
            ].map((spec, index) => (
              <motion.div 
                key={spec.label}
                className="spec-item"
                whileHover={{ 
                  scale: 1.03, 
                  y: -2,
                  transition: { type: "spring", stiffness: 300 }
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 + index * 0.1 }}
              >
                <div className="spec-icon-wrapper">
                  <spec.icon className="spec-icon" />
                </div>
                <div className="spec-content">
                  <span className="spec-label">{spec.label}</span>
                  <span className="spec-value">{spec.value}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Section Équipements */}
        <motion.section 
          className="features-card glass-card"
          variants={cardVariants}
          whileHover={{ y: -3, scale: 1.01 }}
        >
          <div className="card-header">
            <div className="icon-wrapper">
              <FaCog className="card-icon" />
            </div>
            <h3 className="section-title">Équipements</h3>
          </div>
          <div className="features-grid">
            {visibleEquipements.map((equipement, i) => (
              <motion.div 
                key={i}
                className="feature-item"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1 + i * 0.1 }}
                whileHover={{ 
                  x: 8,
                  transition: { type: "spring", stiffness: 200 }
                }}
              >
                <motion.div 
                  className="feature-dot"
                  whileHover={{ scale: 1.5 }}
                />
                <span>{equipement}</span>
              </motion.div>
            ))}
            {equipements.length === 0 && (
              <div className="no-features">
                Aucun équipement spécifié
              </div>
            )}
          </div>
          
          {equipements.length > 6 && (
            <motion.button 
              className="show-more-button"
              onClick={() => setShowAllEquipements(!showAllEquipements)}
              whileHover={{ scale: 1.02, x: 5 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>
                {showAllEquipements 
                  ? 'Voir moins' 
                  : `Voir ${equipements.length - 6} équipements supplémentaires`
                }
              </span>
              <motion.div
                animate={{ rotate: showAllEquipements ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <FiArrowRight />
              </motion.div>
            </motion.button>
          )}
        </motion.section>

        {/* Section Options */}
        <motion.section 
          className="features-card glass-card"
          variants={cardVariants}
          whileHover={{ y: -3, scale: 1.01 }}
        >
          <div className="card-header">
            <div className="icon-wrapper">
              <FaPalette className="card-icon" />
            </div>
            <h3 className="section-title">Options</h3>
          </div>
          <div className="features-grid">
            {visibleOptions.map((option, i) => (
              <motion.div 
                key={i}
                className="feature-item"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2 + i * 0.1 }}
                whileHover={{ 
                  x: 8,
                  transition: { type: "spring", stiffness: 200 }
                }}
              >
                <motion.div 
                  className="feature-dot"
                  whileHover={{ scale: 1.5 }}
                />
                <span>{option}</span>
              </motion.div>
            ))}
            {options.length === 0 && (
              <div className="no-features">
                Aucune option spécifiée
              </div>
            )}
          </div>
          
          {options.length > 6 && (
            <motion.button 
              className="show-more-button"
              onClick={() => setShowAllOptions(!showAllOptions)}
              whileHover={{ scale: 1.02, x: 5 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>
                {showAllOptions 
                  ? 'Voir moins' 
                  : `Voir ${options.length - 6} options supplémentaires`
                }
              </span>
              <motion.div
                animate={{ rotate: showAllOptions ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <FiArrowRight />
              </motion.div>
            </motion.button>
          )}
        </motion.section>

        {/* Description */}
        <motion.section 
          className="description-card glass-card"
          variants={cardVariants}
          whileHover={{ y: -3, scale: 1.01 }}
        >
          <div className="card-header">
            <div className="icon-wrapper">
              <FaInfoCircle className="card-icon" />
            </div>
            <h3 className="section-title">Conditions de location </h3>
          </div>
          <p className="description-text text-left">
            ¨ Avec chauffeur <br />
            ¨ Carburant à charge du locataire <br />
          </p>
           <p className="description-text text-left">
           Les formules proposées couvrent les trajets dans l'agglomération de Kinshasa. 
          </p>
           <p className="description-text text-left">
           Les réservations doivent être effectuées au plus tard 48h à l'avance. Passé ce délai, un supplément de 50$ sera facturé. 
          </p>

           <p className="description-text text-left">
           Nous restons à votre disposition pour toute information complémentaire.
          </p>
        </motion.section>
      </div>

      {/* Actions flottantes élégantes */}
      <motion.div 
        className="floating-actions"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, type: "spring" }}
      >
        <motion.div 
          className="price-bubble"
          whileHover={{ scale: 1.05 }}
        >
          <span className="bubble-price">{car.prix?.toLocaleString()} $</span>
        </motion.div>
        <div className="action-buttons-group">
          <motion.div  
            className="primary-action"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link className='text-decoration-none text-white' to={`/reservation-auto/${car.id}/${car.statut}`}>
            <FiCheckSquare className="button-icon" /> Reserver
            
            </Link>
          </motion.div>
          <motion.div 
            className="secondary-action"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
             <Link className='text-decoration-none text-white' to={`/paiements/${car.id}/${car.statut}`}>
            <FiDollarSign className="button-icon" />
            Paiement
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProfilCarComp;